// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Pure helpers for the speaker grid: how many columns to use for N cards, and
 * audio-level shaping for the speaker rings. Kept side-effect-free so they are
 * unit-tested without a DOM or a live call.
 */

/** Choose a column count that keeps speaker cards balanced for `n` people. */
export function computeGridColumns(n: number): number {
  if (n <= 1) return 1;
  if (n === 2) return 2;
  if (n <= 4) return 2;
  if (n <= 6) return 3;
  if (n <= 9) return 3;
  if (n <= 12) return 4;
  return Math.ceil(Math.sqrt(n));
}

/** Rows implied by `n` cards at the chosen column count. */
export function computeGridRows(n: number): number {
  if (n <= 0) return 0;
  return Math.ceil(n / computeGridColumns(n));
}

/**
 * Exponential level smoothing with a fast attack and slow release, so the
 * speaker ring jumps up on speech but decays gently. `sample` and `prev` are 0..1.
 */
export function smoothLevel(prev: number, sample: number, attack = 0.5, release = 0.12): number {
  const clamped = sample < 0 ? 0 : sample > 1 ? 1 : sample;
  const coeff = clamped > prev ? attack : release;
  return prev + (clamped - prev) * coeff;
}

/** Map an RMS amplitude (0..1) onto a perceptual 0..1 for the ring, with a floor gate. */
export function levelFromRms(rms: number, gate = 0.008): number {
  if (rms < gate) return 0;
  // ~sqrt curve so quiet speech is visible without clipping loud speech.
  const v = Math.sqrt(rms) * 1.6;
  return v > 1 ? 1 : v;
}

/** Whether a smoothed level should count as "actively speaking" (drives the halo). */
export function isSpeaking(level: number, threshold = 0.16): boolean {
  return level > threshold;
}
